import classNames from 'classnames'
import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'

import styles from './greenhouse.module.scss'
import type { GreenHouseJob } from '@server/helpers/greenhouse'

const GREENHOUSE_SCRIPT_URL =
    'https://boards.greenhouse.io/embed/job_board/js?for=stepmobile'
const MAX_RETRIES = 5
const RETRY_INTERVAL = 500
const TIMEOUT = 500

// function to check if script is loaded
const isScriptLoaded = (src: string) => {
    return !!document.querySelector(`script[src="${src}"]`)
}

// function to sync load script if not loaded
const loadScript = (src: string) => {
    return new Promise((resolve) => {
        const script = document.createElement('script')
        script.src = src
        script.async = true
        script.onload = () => {
            resolve(true)
        }

        document.body.appendChild(script)
    })
}

const waitForGrnhse = () => {
    return new Promise((resolve, reject) => {
        let attempts = 0
        const startTime = Date.now()

        const check = () => {
            if (typeof window !== 'undefined' && window.Grnhse?.Iframe?.load) {
                resolve(true)
                return
            }

            attempts++
            if (attempts >= MAX_RETRIES || Date.now() - startTime > TIMEOUT) {
                reject(new Error('Greenhouse script failed to initialize'))
                return
            }

            setTimeout(check, RETRY_INTERVAL)
        }

        check()
    })
}

export const GreenHouseForm: FunctionComponent<GreenHouseJob> = ({ id }) => {
    const [frameLoading, setFrameLoading] = useState(true)
    const [error, setError] = useState(false)

    const init = async () => {
        try {
            if (!isScriptLoaded(GREENHOUSE_SCRIPT_URL)) {
                await loadScript(GREENHOUSE_SCRIPT_URL)
            }

            await waitForGrnhse()
            const loaded = window.Grnhse.Iframe.load(id)

            if (loaded) {
                setFrameLoading(false)
            } else {
                throw new Error('Failed to load iframe')
            }
        } catch (e) {
            console.error('Error loading Greenhouse form:', e)
            setFrameLoading(false)
            setError(true)
        }
    }

    useEffect(() => {
        const timer = setTimeout(init, 500)
        return () => clearTimeout(timer)
    }, [id])

    if (error) {
        return (
            <div className={styles.error}>
                Unable to load application form. Please try reloading the page.
            </div>
        )
    }

    return (
        <div
            className={classNames(styles.form, {
                [styles['form-loading']]: frameLoading,
            })}
        >
            <div id='grnhse_app' />
        </div>
    )
}
